import { sha256Hex, type Notice } from './notice.js';
import type { ConsentState } from './types.js';

/**
 * 동의를 **어딘가에 기록**하는 자리.
 *
 * ## 왜 인터페이스로 두나
 *
 * 보관처는 B안(RAYConsent 적재)으로 정해졌다 (명세 §2, PL 2026-10-14) —
 * 구현은 `rayConsent` 다. 로컬 저장은 어느 안이든 공통이고 차이는 **서버로
 * 보내느냐**뿐이라 그 부분만 떼어 뒀고, **배너 본체는 바뀌지 않았다.**
 *
 * ## 기록 실패가 배너를 막지 않는다
 *
 * 이용자는 이미 선택했다. 그 선택은 로컬에 저장되고 **즉시 유효하다.**
 * 서버 기록이 실패했다고 다시 묻거나 화면을 막으면, **우리 장애가 사이트
 * 이용을 막는다.**
 *
 * 같은 실수가 동의 기록 쪽에서 실제로 났다 — 적재 함수를 「절대 throw 하지
 * 않는다」로 설계해 놓고 **설정 로드를 try/catch 밖에** 둬서, 시크릿 조회
 * 실패가 본래 기능까지 죽였다.
 */
export interface Recorder {
  /**
   * **절대 throw 하지 않는다.** 실패는 `false` 로 알리고 끝낸다.
   * 설정 로드·식별자 파생도 전부 안에서 감싼다.
   */
  record(state: ConsentState, notice: Notice): Promise<boolean>;
}

/**
 * 아무 데도 보내지 않는다. **기본값**이다.
 *
 * 적재는 사이트가 `recorder: rayConsent(...)` 로 켠다. 기본을 적재로 두면
 * 등록하지 않은 사이트가 붙는 순간 **거부될 요청을 매번 보낸다.**
 */
export const localOnly: Recorder = {
  record: async () => true,
};

/**
 * 여러 곳에 보낸다. 하나가 실패해도 **나머지는 계속한다.**
 *
 * 「전부 성공했는가」를 돌려주되, **실패가 호출자를 멈추지는 않는다.**
 */
export function all(recorders: readonly Recorder[]): Recorder {
  return {
    record: async (state, notice) => {
      const results = await Promise.all(
        recorders.map(async (r) => {
          try {
            return await r.record(state, notice);
          } catch {
            return false;
          }
        }),
      );
      return results.every(Boolean);
    },
  };
}

/**
 * **변경·철회만** 보낸다 — 명세 §2 의 C안.
 *
 * 최초 동의는 로컬에만 두고, **이미 정한 뒤 바뀐 것**만 기록한다. 분쟁이
 * 실제로 생기는 지점이 「철회했는데 계속 썼다」라서, 거기만 증거로 남긴다.
 *
 * 최초 동의까지 기록하려면 **방문자마다 식별자를 만들어야 하고**, 그것은
 * 추적하지 않으려고 받는 동의를 위해 추적자를 만드는 일이 된다.
 */
export function changesOnly(inner: Recorder): Recorder {
  let last: string | null = null;
  return {
    record: async (state, notice) => {
      const key = JSON.stringify(state.choices);
      const first = last === null;
      last = key;
      // 첫 기록은 보내지 않는다. 그 뒤 **달라졌을 때만** 보낸다.
      if (first) return true;
      return inner.record(state, notice);
    },
  };
}

export interface RayConsentOptions {
  /** 수신 엔드포인트. 기본 `https://static.rayplatform.com/rcb/record`. */
  readonly endpoint?: string;
}

export const DEFAULT_ENDPOINT = 'https://static.rayplatform.com/rcb/record';

/**
 * **RAYConsent 에 적재한다** — 명세 §2 의 B안.
 *
 * 배너는 키를 쥘 수 없다 (설계 불변 조건 4). 그래서 **키를 쥔 수신
 * 엔드포인트**로 보내고, 그쪽이 사이트 출처로 테넌트를 고르고, 문안 해시로
 * 등록 판을 찾아 적재한다. 여기서 보내는 것은 **선택과 그 선택을 받을 때
 * 보여 준 문안의 해시**뿐이다.
 *
 * - **필수 목적은 보내지 않는다** — 동의 대상이 아니다
 * - **IP 를 보내지 않는다** — 요청에는 당연히 실리지만 수신 측이 저장하지 않는다
 * - 쿼리 문자열은 보내지 않는다 — 토큰·이메일이 실려 오는 일이 있다
 *
 * 본문을 `text/plain` 으로 보낸다. JSON 으로 보내면 매번 사전 요청(preflight)이
 * 붙어 왕복이 둘이 된다. 출처 검사는 수신 측이 `Origin` 으로 한다.
 */
export function rayConsent(options: RayConsentOptions = {}): Recorder {
  const endpoint = options.endpoint ?? DEFAULT_ENDPOINT;
  return {
    record: async (state, notice) => {
      try {
        if (typeof state.subjectId !== 'string') return false;
        const choices: Record<string, string> = {};
        for (const [id, c] of Object.entries(state.choices)) {
          if (!notice.essential.includes(id)) choices[id] = c;
        }
        // 고를 것이 없었다면 동의가 아니다.
        if (Object.keys(choices).length === 0) return true;

        const body = JSON.stringify({
          subject_id: state.subjectId,
          text_id: notice.textId,
          locale: notice.locale,
          text_sha256: await sha256Hex(notice.text),
          choices,
          regime: state.regime,
          decided_at: state.decidedAt,
          page: location.pathname.slice(0, 200),
        });
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          body,
          // 선택 직후 이용자가 페이지를 떠나도 끝까지 보낸다.
          keepalive: true,
          credentials: 'omit',
          mode: 'cors',
        });
        return res.ok;
      } catch {
        return false;
      }
    },
  };
}
